import { useCallback, useState } from 'react';
import { Items } from '../../../../../../types';
import { buildFiltersObject } from '../../../helpers/buildFiltersObject';
import { fetchDownloadData } from '../../../apiCalls';
import { getActiveColumnFilters } from '../../../utils/activeColumnFilters';
import {
  buildSummaryMatchStats,
  extractSummaryMatchApiParams,
  getEvidenceDownloadColumnsForMatch,
  SummaryMatchApiParams,
  SummaryMatchStats,
  withForcedDownloadColumns,
} from '../../../utils/matchEvidenceToSummary';
import { MappingContent, SearchItem } from '../types';

type UseSummaryMatchParams = {
  view: string;
  source: string;
  tab: string;
  searchItems: SearchItem[];
  isAuthenticated: boolean;
  authorization: unknown;
  isMapping?: boolean;
  mappingContent?: MappingContent;
  sourceWeights?: Array<{ name: string; score: number }>;
  evidenceColumnVisibilityModel?: Record<string, boolean>;
  getFilterValueRef: React.MutableRefObject<(() => Array<{ name: string; value: unknown }>) | null>;
  dispatch: (action: { type: string; payload?: boolean }) => void;
  onWarning: (message: string) => void;
  onError: (message: string) => void;
  onMatchComplete?: () => void;
};

function toApiSearchItems(searchItems: SearchItem[]): Items[] {
  return searchItems.map(item => ({
    id: item.id,
    name: item.name,
    [item.id]: item.name,
  }));
}

export function useSummaryMatch({
  view,
  source,
  tab,
  searchItems,
  isAuthenticated,
  authorization,
  isMapping = false,
  mappingContent,
  sourceWeights = [],
  evidenceColumnVisibilityModel,
  getFilterValueRef,
  dispatch,
  onWarning,
  onError,
  onMatchComplete,
}: UseSummaryMatchParams) {
  const [isLoading, setIsLoading] = useState(false);
  const [matchApiParams, setMatchApiParams] = useState<SummaryMatchApiParams | null>(null);
  const [stats, setStats] = useState<SummaryMatchStats | null>(null);

  const openSummaryMatch = useCallback(async () => {
    const rawFilters = getFilterValueRef.current?.() ?? [];
    const activeFilters = getActiveColumnFilters(rawFilters);

    if (activeFilters.length === 0) {
      onError('Apply at least one filter to view matching Summary associations.');
      return;
    }

    if (searchItems.length > 50) {
      onError('Cannot process more than 50 search items. Please reduce your selection.');
      return;
    }

    const downloadColumns = getEvidenceDownloadColumnsForMatch(tab, view, searchItems);
    const evidenceDownloadModel = withForcedDownloadColumns(evidenceColumnVisibilityModel, downloadColumns);

    const apiSearchItems = toApiSearchItems(searchItems);
    const apiMappingContent = mappingContent
      ? {
          isActive: mappingContent.isActive,
          columVisibilityModelKey: mappingContent.columVisibilityModelKey,
          mappingItems: mappingContent.mappingItems?.map(item => ({
            filterParam: null,
            filterValue: item.id,
          })),
        }
      : undefined;

    setIsLoading(true);
    setMatchApiParams(null);
    setStats(null);
    onWarning('Building Summary match from filtered Evidence…');
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const evidenceRows = await fetchDownloadData({
        columnVisibilityModel: evidenceDownloadModel,
        view,
        source,
        tab,
        searchItems: apiSearchItems,
        isAuthenticated,
        authorization,
        isEvidence: true,
        isMapping,
        mappingContent: apiMappingContent,
        filters: buildFiltersObject(activeFilters as Parameters<typeof buildFiltersObject>[0]),
        sourceWeights,
      });

      const extracted = extractSummaryMatchApiParams(evidenceRows, tab, view, searchItems);

      if (!extracted) {
        onError(
          'Could not extract association identifiers from filtered Evidence. Ensure gene, disease, or variant columns are available.',
        );
        return;
      }

      if (extracted.params && Object.values(extracted.params)[0]?.length === 0) {
        onError('No Summary associations match the current filtered Evidence. Try adjusting your filters.');
        return;
      }

      const matchStats = buildSummaryMatchStats(evidenceRows, tab, extracted);

      setMatchApiParams(extracted.params);
      setStats(matchStats);
      onMatchComplete?.();
    } catch (err) {
      console.error('Summary match failed:', err);
      onError('Could not build the Summary match. Please try again.');
    } finally {
      setIsLoading(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    authorization,
    dispatch,
    evidenceColumnVisibilityModel,
    getFilterValueRef,
    isAuthenticated,
    isMapping,
    mappingContent,
    onError,
    onMatchComplete,
    onWarning,
    searchItems,
    source,
    sourceWeights,
    tab,
    view,
  ]);

  const clearSummaryMatch = useCallback(() => {
    setMatchApiParams(null);
    setStats(null);
  }, []);

  const isActive = matchApiParams != null && Object.keys(matchApiParams).length > 0;

  return {
    isLoading,
    isActive,
    matchApiParams,
    stats,
    openSummaryMatch,
    clearSummaryMatch,
  };
}
